import { useSelector } from "react-redux";

import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { useTableSelections } from "app/pages/sales-intelligence/modules/company-analysis/hooks/useTableSelections";
import { useDownloadCompetitorsExcelMutation } from "app/pages/sales-intelligence/modules/company-analysis/queries/useDownloadCompetitorsExcelMutation";
import exportSelectionDropdown from "app/pages/sales-intelligence/modules/company-analysis/services/actions-panel/exportSelectionDropdown";
import {
    selectFilteredCompetitorsDomains,
    selectSelectedCompetitorsDomains,
} from "app/pages/sales-intelligence/modules/company-analysis/store/competitors/selectors";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import { useLeadsExportCost } from "app/pages/sales-intelligence/modules/quota/hooks/useLeadsExportCost";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { selectExcelQuotaRemaining } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import LegacyWebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/LegacyWebsitesExcelExportPanel/LegacyWebsitesExcelExportPanelView";
import WebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/WebsitesExcelExportPanel/WebsitesExcelExportPanelView";
import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/components/BaseActivatedPanel/types";
import type { SelectionDropdownItem } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/models/selection-dropdown-item/types";

const ExcelCompetitorsExportPanel = (props: BaseActivatedPanelProps) => {
    const { params } = useCurrentStateAndParamsWithFixedDomain();
    const selectedCompetitorsDomains = useSelector(selectSelectedCompetitorsDomains);
    const filteredCompetitorsDomains = useSelector(selectFilteredCompetitorsDomains);

    const { mutate, isPending } = useDownloadCompetitorsExcelMutation();

    const excelQuotaRemaining = useSelector(selectExcelQuotaRemaining);

    const exportCostState = useLeadsExportCost({ exportType: ExportCostTypeItem.Competitors });

    const { clearAndCloseRowsSelection, onSelectionChange } = useTableSelections();

    const handleDeactivate = () => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSubmit = (item: Readonly<SelectionDropdownItem>) => {
        trackingService.trackMultiSelectorExportClick("excel", excelQuotaRemaining, item.alias, item.value);
        handleDeactivate();
        mutate({
            domain: params.key,
            competitors:
                item.id === ManualSelectionItem.ID
                    ? selectedCompetitorsDomains
                    : filteredCompetitorsDomains.slice(0, item.value),
        });
    };

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesExcelExportPanelView
                isDisabled={isPending}
                isLoading={isPending}
                numberOfSelectedItems={selectedCompetitorsDomains.length}
                numberOfRemainingExports={excelQuotaRemaining}
                numberOfResults={filteredCompetitorsDomains.length}
                numberOfTableColumns={0}
                selectionDropdown={exportSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={onSelectionChange}
                onSubmit={handleSubmit}
                exportType={ExportCostTypeItem.Competitors}
                exportCostState={exportCostState}
            />
        );
    }

    return (
        <LegacyWebsitesExcelExportPanelView
            isDisabled={isPending}
            isLoading={isPending}
            numberOfSelectedItems={selectedCompetitorsDomains.length}
            numberOfRemainingExports={excelQuotaRemaining}
            numberOfResults={filteredCompetitorsDomains.length}
            numberOfTableColumns={0}
            selectionDropdown={exportSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={onSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default ExcelCompetitorsExportPanel;
